import type { AthenaAuthImplementedDomainEvent } from "../hooks/events.ts";

export interface AthenaAuthEventSubject {
	id: string;
	type: string;
}

export interface AthenaAuthEventDefinition {
	audit: boolean;
	subjectType: string;
}

/**
 * Explicit event → audit policy. Implemented mutations default to audited
 * security history. Reads never appear here.
 */
export const ATHENA_AUTH_EVENT_DEFINITIONS = {
	"apiKey.create": { audit: true, subjectType: "api_key" },
	"apiKey.delete": { audit: true, subjectType: "api_key" },
	"apiKey.update": { audit: true, subjectType: "api_key" },
	"organization.create": { audit: true, subjectType: "organization" },
	"organization.delete": { audit: true, subjectType: "organization" },
	"organization.invitation.accept": {
		audit: true,
		subjectType: "organization.invitation",
	},
	"organization.invitation.cancel": {
		audit: true,
		subjectType: "organization.invitation",
	},
	"organization.invitation.create": {
		audit: true,
		subjectType: "organization.invitation",
	},
	"organization.invitation.reject": {
		audit: true,
		subjectType: "organization.invitation",
	},
	"organization.member.add": {
		audit: true,
		subjectType: "organization.member",
	},
	"organization.member.invite.reminder": {
		audit: true,
		subjectType: "organization.invitation",
	},
	"organization.member.remove": {
		audit: true,
		subjectType: "organization.member",
	},
	"organization.member.role.update": {
		audit: true,
		subjectType: "organization.member",
	},
	"organization.update": { audit: true, subjectType: "organization" },
	"passkey.delete": { audit: true, subjectType: "passkey" },
	"passkey.register": { audit: true, subjectType: "passkey" },
	"passkey.update": { audit: true, subjectType: "passkey" },
	"session.activeOrganization.update": {
		audit: true,
		subjectType: "session",
	},
	"session.impersonation.end": { audit: true, subjectType: "session" },
	"session.impersonation.start": { audit: true, subjectType: "session" },
	"session.issue": { audit: true, subjectType: "session" },
	"session.revoke": { audit: true, subjectType: "session" },
	"twoFactor.disable": { audit: true, subjectType: "user" },
	"twoFactor.enable": { audit: true, subjectType: "user" },
	"user.ban": { audit: true, subjectType: "user" },
	"user.create": { audit: true, subjectType: "user" },
	"user.security.alert": { audit: true, subjectType: "user" },
	"user.sign-in.email": { audit: true, subjectType: "user" },
	"user.delete": { audit: true, subjectType: "user" },
	"user.email.update": { audit: true, subjectType: "user" },
	"user.email.verify": { audit: true, subjectType: "user" },
	"user.password.change": { audit: true, subjectType: "user" },
	"user.password.reset": { audit: true, subjectType: "user" },
	"user.role.update": { audit: true, subjectType: "user" },
	"user.unban": { audit: true, subjectType: "user" },
	"user.update": { audit: true, subjectType: "user" },
} as const satisfies Record<
	AthenaAuthImplementedDomainEvent,
	AthenaAuthEventDefinition
>;

function asRecord(value: unknown): Record<string, unknown> | null {
	if (!value || typeof value !== "object" || Array.isArray(value)) {
		return null;
	}
	return value as Record<string, unknown>;
}

function nestedId(value: unknown, key: string): string | undefined {
	const record = asRecord(value);
	const nested = asRecord(record?.[key]);
	const id = nested?.id;
	return typeof id === "string" && id.trim() ? id : undefined;
}

function stringField(value: unknown, key: string): string | undefined {
	const record = asRecord(value);
	const field = record?.[key];
	return typeof field === "string" && field.trim() ? field : undefined;
}

export function isAuditedAuthEvent(
	event: AthenaAuthImplementedDomainEvent,
): boolean {
	return ATHENA_AUTH_EVENT_DEFINITIONS[event].audit;
}

export function resolveAuthEventSubject(
	event: AthenaAuthImplementedDomainEvent,
	result: unknown,
): AthenaAuthEventSubject | undefined {
	const type = ATHENA_AUTH_EVENT_DEFINITIONS[event].subjectType;
	const id =
		nestedId(result, "user") ??
		nestedId(result, "organization") ??
		nestedId(result, "member") ??
		nestedId(result, "invitation") ??
		nestedId(result, "session") ??
		nestedId(result, "apiKey") ??
		nestedId(result, "passkey") ??
		stringField(result, "id") ??
		stringField(result, "userId") ??
		stringField(result, "organizationId") ??
		stringField(result, "sessionId");
	if (!id) {
		return undefined;
	}
	return { id, type };
}
